'use client'

import { useCallback, useMemo, useState } from 'react'
import { useTranslations } from "next-intl";
import { cn, TAddressFormValues } from '@segway/core'
import { CountryCodeEnum, CustomerAddressInput } from '@segway/core/src/graphql/generated/graphql'

import { AddressFieldComparison } from '@/app/[locale]/customer/address/_utils'
import { Dialog } from '@/components'
import { Button, Label, RadioGroup, RadioGroupItem } from '@/ui'

export type AddressValidationDialogProps = {
  isOpen: boolean
  address: TAddressFormValues | null
  improvedAddress?: CustomerAddressInput | null
  addressComparison?: AddressFieldComparison[] | null
  title?: string | null
  onClose: () => void
  errorMessage?: string | null
  onSaveAddress?: (address: CustomerAddressInput) => void
}

export const CheckoutAddressValidationDialog = ({
  isOpen,
  address,
  improvedAddress,
  addressComparison,
  title,
  onClose,
  errorMessage,
  onSaveAddress,
}: AddressValidationDialogProps) => {
  const [selectedAddressType, setSelectedAddressType] = useState<string>('improved') // 'improved' | 'original'
  const tCommon = useTranslations('Common')
  const tAddress = useTranslations('Address')

  const isError = useMemo(() => {
    return !!errorMessage
  }, [errorMessage])

  const handleSave = useCallback(() => {
    if (onSaveAddress && improvedAddress && address) {
      onSaveAddress(
        selectedAddressType === 'improved'
          ? improvedAddress
          : {
              city: address.city,
              company: address.company,
              country_id: address.country_code as CountryCodeEnum,
              country_code: address.country_code as CountryCodeEnum,
              firstname: address.firstname,
              lastname: address.lastname,
              postcode: address.postcode,
              region: {
                region: address.region,
                region_id: Number(address.region_id),
              },
              street: address.street,
              telephone: address.telephone,
            },
      )
    }
  }, [onSaveAddress, selectedAddressType, improvedAddress, address])

  return (
    <Dialog
      open={isOpen}
      onOpenChange={onClose}
      containerStyle="w-[370px] xl:w-[600px] md:w-[600px] lg:w-[600px] rounded-lg">
      <div className="px-6 py-4">
        <h3 className="text-xl font-semibold">
          {title || (isError ? tCommon('address_suggestion_error') : tCommon('address_suggestion'))}
        </h3>

        {isError ? (
          <div className="mt-4">
            <p>{tAddress('address_validation_failed')}</p>
            <div className="mt-2 rounded-md border border-[#E1E1E4] bg-[#f5f5f5] p-3 font-miSansRegular330">
              {errorMessage}
            </div>
            <p className="mt-4">
              {tAddress('address_confirm_info')}
            </p>
            <div className="mt-2 rounded-md border border-[#E1E1E4] bg-[#f5f5f5] p-3 font-miSansRegular330">
              <div className="mt-1">
                {address?.firstname} {address?.lastname} {address?.telephone}
              </div>
              <div className="mt-1">
                {address?.street?.[0] && address?.street?.[0]}
                {address?.street?.[1] && `, ${address?.street?.[1]}`}
                {address?.street?.[2] && `, ${address?.street?.[2]}`}
              </div>
              <div className="mt-1">
                {address?.city} {address?.country_code} {address?.postcode}
              </div>
            </div>
          </div>
        ) : (
          <div className="mt-4">
            <p className="mb-4">
              {tAddress('address_suggestion_message')}
            </p>
            <RadioGroup
              value={selectedAddressType}
              onValueChange={(value) => setSelectedAddressType(value)}
              className="grid-cols-1 gap-4 space-y-4 xl:grid-cols-2">
              <div
                className={cn(
                  '!mt-0 flex cursor-pointer items-start space-x-2 rounded-md border border-transparent p-3',
                  selectedAddressType === 'improved' ? 'border-[#E1E1E4]' : '',
                )}>
                <RadioGroupItem value="improved" id="improved" />
                <div>
                  <Label htmlFor="improved" className="font-medium">
                    {tCommon('suggested_address')}
                    {addressComparison &&
                      addressComparison.length > 0 &&
                      addressComparison.map((item) => {
                        const isImproved = item.improved !== item.original
                        return (
                          <div
                            key={item.field}
                            className={`mt-2 text-sm ${isImproved ? 'bg-[#93ee9e] font-medium' : ''}`}>
                            {item.improved}
                          </div>
                        )
                      })}
                  </Label>
                </div>
              </div>

              <div
                className={cn(
                  '!mt-0 flex cursor-pointer items-start space-x-2 rounded-md border border-transparent p-3',
                  selectedAddressType === 'original' ? 'border-[#E1E1E4]' : '',
                )}>
                <RadioGroupItem value="original" id="original" />
                <div>
                  <Label htmlFor="original" className="font-medium">
                    {tCommon('original_address')}
                    {addressComparison &&
                      addressComparison.length > 0 &&
                      addressComparison.map((item) => {
                        return (
                          <div key={item.field} className="mt-2 text-sm">
                            {item.original}
                          </div>
                        )
                      })}
                  </Label>
                </div>
              </div>
            </RadioGroup>
          </div>
        )}

        <div className="mt-6 flex justify-end space-x-2">
          <div className="flex items-center gap-2">
            <Button variant="secondary" onClick={onClose}>
              {tCommon('edit_address')}
            </Button>
            {!isError && <Button onClick={handleSave}>{tCommon('save_address')}</Button>}
          </div>
        </div>
      </div>
    </Dialog>
  )
}

export default CheckoutAddressValidationDialog
